import { useEffect, useState } from "react";
import { Warning, ShieldWarning } from "@phosphor-icons/react";

let _request = null;

/** Drop-in replacement for window.confirm — returns Promise<boolean>. */
export function confirmDialog(message, opts = {}) {
    return new Promise((resolve) => {
        if (_request) _request({ message, opts, resolve });
        else resolve(window.confirm(message));
    });
}

/** Drop-in replacement for window.prompt — resolves to the string, or null on cancel. */
export function promptDialog(message, opts = {}) {
    return new Promise((resolve) => {
        if (_request) _request({ message, opts: { ...opts, input: true, danger: opts.danger ?? false }, resolve });
        else resolve(window.prompt(message, opts.defaultValue || ""));
    });
}

export function ConfirmDialogHost() {
    const [state, setState] = useState(null);
    const [inputVal, setInputVal] = useState("");
    useEffect(() => {
        _request = (r) => setState((prev) => {
            if (prev) prev.resolve(prev.opts?.input ? null : false);
            setInputVal(r.opts?.defaultValue || "");
            return r;
        });
        return () => { _request = null; };
    }, []);

    useEffect(() => {
        if (!state) return;
        const isInput = !!state.opts?.input;
        const onKey = (e) => {
            if (e.key === "Escape") { state.resolve(isInput ? null : false); setState(null); }
            if (e.key === "Enter") { state.resolve(isInput ? inputVal : true); setState(null); }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [state, inputVal]);

    if (!state) return null;
    const isInput = !!state.opts?.input;
    const { title = isInput ? "Input needed" : "Are you sure?", confirmText = "confirm", cancelText = "cancel", danger = !isInput, placeholder = "" } = state.opts || {};
    const close = (ok) => { state.resolve(isInput ? (ok ? inputVal : null) : ok); setState(null); };

    return (
        <div className="fixed inset-0 z-[120] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
             onClick={() => close(false)} data-testid="confirm-dialog" role="alertdialog" aria-modal="true">
            <div onClick={(e) => e.stopPropagation()}
                 className={`w-full max-w-md bg-[#0b0f19] border rounded-md p-5 shadow-2xl ${danger ? "border-rose-500/40" : "border-cyan-500/40"}`}>
                <div className="flex items-start gap-3">
                    <div className={`shrink-0 w-9 h-9 rounded-sm border flex items-center justify-center ${danger ? "border-rose-500/40 bg-rose-500/10 text-rose-400" : "border-cyan-500/40 bg-cyan-500/10 text-cyan-400"}`}>
                        {danger ? <ShieldWarning size={18} weight="duotone" /> : <Warning size={18} weight="duotone" />}
                    </div>
                    <div className="min-w-0 flex-1">
                        <h3 className="text-sm font-bold text-slate-100" style={{ fontFamily: "'Space Grotesk', 'Chivo', sans-serif" }} data-testid="confirm-dialog-title">{title}</h3>
                        <p className="text-[13px] text-slate-300 mt-1.5 leading-relaxed whitespace-pre-line" data-testid="confirm-dialog-message">{state.message}</p>
                        {isInput && (
                            <input value={inputVal} onChange={(e) => setInputVal(e.target.value)} placeholder={placeholder} autoFocus
                                   data-testid="confirm-dialog-input"
                                   className="w-full mt-3 bg-slate-900 border border-slate-700 rounded-sm px-2.5 py-2 text-sm text-slate-200 focus:border-cyan-500/60 outline-none" />
                        )}
                    </div>
                </div>
                <div className="flex items-center justify-end gap-2 mt-5">
                    <button onClick={() => close(false)} data-testid="confirm-dialog-cancel"
                            className="text-[10px] uppercase tracking-widest font-mono text-slate-400 hover:text-slate-200 border border-slate-700 hover:border-slate-500 px-4 py-2 rounded-sm transition-colors">
                        {cancelText}
                    </button>
                    <button onClick={() => close(true)} autoFocus={!isInput} data-testid="confirm-dialog-confirm"
                            className={`text-[10px] uppercase tracking-widest font-mono font-bold px-4 py-2 rounded-sm transition-colors ${danger ? "bg-rose-500 hover:bg-rose-400 text-white" : "bg-cyan-400 hover:bg-cyan-300 text-slate-950"}`}>
                        {confirmText}
                    </button>
                </div>
            </div>
        </div>
    );
}
